// Document Folders Management
// Provides folder hierarchy UI with drag-and-drop for organizing documents

console.log('[DocumentFolders] Module loaded');

class DocumentFolderManager {
  constructor(assetId, containerId) {
    this.assetId = assetId;
    this.containerId = containerId;
    this.folders = [];
    this.expandedFolders = new Set();
    this.selectedFolder = null;
  }

  async init() {
    console.log('DocumentFolderManager init() called');
    await this.loadFolders();
    console.log('About to render folders...');
    this.render();
    this.attachEventListeners();
  }

  async loadFolders() {
    try {
      const response = await fetch(`/assets/${this.assetId}/document_folders`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        this.folders = await response.json();
        console.log('Folders loaded:', this.folders);
      } else {
        console.error('Failed to load folders:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  }

  render() {
    const tbody = document.querySelector('.docs-table tbody');
    if (!tbody) {
      console.log('Cannot render: tbody not found');
      return;
    }

    console.log('Rendering folders, count:', this.folders.length);

    // Remove existing folder rows (including add folder row)
    tbody.querySelectorAll('tr.folder-row, tr.add-folder-row').forEach(row => row.remove());

    // Insert folder rows at the top
    const folderRows = this.renderFolderRows(this.folders);
    console.log('Generated HTML length:', folderRows.length, 'characters');
    console.log('First 200 chars:', folderRows.substring(0, 200));

    tbody.insertAdjacentHTML('afterbegin', folderRows);

    console.log('Folder rows in DOM:', tbody.querySelectorAll('tr.folder-row, tr.add-folder-row').length);

    // Now position and show/hide document rows based on folder state
    this.organizeDocumentRows();

    this.attachFolderEventListeners();
  }

  renderFolderRows(folders, level = 0) {
    if (!folders || folders.length === 0) {
      return level === 0 ? this.renderAddFolderRow() : '';
    }

    let rows = level === 0 ? this.renderAddFolderRow() : '';

    folders.forEach(folder => {
      const isExpanded = this.expandedFolders.has(folder.id);
      const isSelected = this.selectedFolder === folder.id;
      const hasSubfolders = folder.subfolders && folder.subfolders.length > 0;
      const hasDocuments = folder.documents && folder.documents.length > 0;
      const hasContent = hasSubfolders || hasDocuments;
      const docCount = folder.documents ? folder.documents.length : 0;
      const indent = level * 30;

      rows += `
        <tr class="folder-row ${isSelected ? 'folder-selected' : ''}"
            data-folder-id="${folder.id}"
            data-folder-path="${folder.path}"
            data-system-folder="${folder.system_folder}"
            data-folder-level="${level}"
            draggable="true">
          <td colspan="2">
            <div class="folder-content" style="padding-left: ${indent}px; display: flex; align-items: center; gap: 8px;">
              ${hasContent ?
                `<i class="bi bi-chevron-${isExpanded ? 'down' : 'right'} folder-toggle" style="cursor: pointer; color: #6c757d;"></i>` :
                `<span style="width: 16px; display: inline-block;"></span>`
              }
              <i class="bi bi-folder${folder.system_folder ? '-fill text-primary' : ''}" style="font-size: 16px;"></i>
              <strong>${folder.name}</strong>
              <span class="badge bg-secondary">${docCount}</span>
            </div>
          </td>
          <td colspan="6"></td>
          <td colspan="2" class="text-end">
            ${!folder.system_folder ? `
              <button class="btn btn-sm btn-link rename-folder" title="Rename">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="btn btn-sm btn-link text-danger delete-folder" title="Delete">
                <i class="bi bi-trash"></i>
              </button>
            ` : ''}
          </td>
        </tr>
      `;

      // Show subfolders if expanded
      if (isExpanded && hasSubfolders) {
        rows += this.renderFolderRows(folder.subfolders, level + 1);
      }
    });

    return rows;
  }

  organizeDocumentRows() {
    const tbody = document.querySelector('.docs-table tbody');
    if (!tbody) return;

    // Get all document rows (not folder rows)
    const allDocRows = Array.from(tbody.querySelectorAll('tr[data-document-id]'));

    // First, hide all documents that belong to folders and reset indentation
    allDocRows.forEach(row => {
      const folderId = row.dataset.folderId;
      if (folderId) {
        // Document belongs to a folder
        row.style.display = 'none';

        // Reset indentation
        const firstCell = row.querySelector('td:first-child');
        if (firstCell && firstCell.dataset.indented === 'true') {
          const indentDiv = firstCell.querySelector('div[style*="padding-left"]');
          if (indentDiv) {
            firstCell.innerHTML = indentDiv.innerHTML;
            firstCell.dataset.indented = 'false';
          }
        }
      }
    });

    // Now, for each expanded folder, show and position its documents
    this.positionDocumentsForFolders(this.folders, tbody);
  }

  positionDocumentsForFolders(folders, tbody, level = 0) {
    if (!folders || folders.length === 0) return;

    folders.forEach(folder => {
      const isExpanded = this.expandedFolders.has(folder.id);

      // Recursively handle subfolders first (so they're processed bottom-up)
      if (folder.subfolders && folder.subfolders.length > 0) {
        this.positionDocumentsForFolders(folder.subfolders, tbody, level + 1);
      }

      if (isExpanded) {
        // Find this folder's row in the DOM
        const folderRow = tbody.querySelector(`tr.folder-row[data-folder-id="${folder.id}"]`);
        if (folderRow) {
          const folderLevel = parseInt(folderRow.dataset.folderLevel) || 0;
          const docIndent = (folderLevel + 1) * 30; // One level deeper than the folder

          // Get all documents for this folder
          const folderDocRows = Array.from(tbody.querySelectorAll(`tr[data-document-id][data-folder-id="${folder.id}"]`));

          // Insert documents right after the folder row
          // Working backwards ensures correct order when moving multiple elements
          for (let i = folderDocRows.length - 1; i >= 0; i--) {
            const docRow = folderDocRows[i];
            docRow.style.display = '';

            // Add indentation to the first cell
            const firstCell = docRow.querySelector('td:first-child');
            if (firstCell && !firstCell.dataset.indented) {
              const originalContent = firstCell.innerHTML;
              firstCell.innerHTML = `<div style="padding-left: ${docIndent}px;">${originalContent}</div>`;
              firstCell.dataset.indented = 'true';
              firstCell.dataset.indentLevel = docIndent;
            }

            // Insert right after the folder row (before any previously inserted docs)
            folderRow.after(docRow);
          }
        }
      }
    });
  }

  renderAddFolderRow() {
    return `
      <tr class="add-folder-row">
        <td colspan="10" style="background-color: #f8f9fa; border-top: 2px solid #dee2e6;">
          <button class="btn btn-sm btn-outline-primary" id="create-folder-btn">
            <i class="bi bi-folder-plus"></i> Add Folder
          </button>
        </td>
      </tr>
    `;
  }

  renderFolderTree(folders, level = 0) {
    if (!folders || folders.length === 0) {
      return level === 0 ? '<p class="text-muted small">No folders yet</p>' : '';
    }

    return folders.map(folder => {
      const isExpanded = this.expandedFolders.has(folder.id);
      const isSelected = this.selectedFolder === folder.id;
      const hasSubfolders = folder.subfolders && folder.subfolders.length > 0;
      const indent = level * 20;
      const docCount = folder.documents ? folder.documents.length : 0;

      return `
        <div class="folder-item ${isSelected ? 'selected' : ''}"
             data-folder-id="${folder.id}"
             data-folder-path="${folder.path}"
             data-system-folder="${folder.system_folder}"
             style="padding-left: ${indent}px;">
          <div class="folder-content" draggable="${!folder.system_folder}">
            ${hasSubfolders ?
              `<i class="bi bi-chevron-${isExpanded ? 'down' : 'right'} folder-toggle"></i>` :
              `<span class="folder-spacer"></span>`
            }
            <i class="bi bi-folder${folder.system_folder ? '-fill text-primary' : ''} folder-icon"></i>
            <span class="folder-name">${folder.name}</span>
            <span class="folder-count badge bg-secondary">${docCount}</span>
            ${!folder.system_folder ? `
              <div class="folder-actions">
                <button class="btn btn-sm btn-link rename-folder" title="Rename">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-link delete-folder" title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            ` : ''}
          </div>
          ${isExpanded && hasSubfolders ? `
            <div class="folder-children">
              ${this.renderFolderTree(folder.subfolders, level + 1)}
            </div>
          ` : ''}
        </div>
      `;
    }).join('');
  }

  attachFolderEventListeners() {
    const tbody = document.querySelector('.docs-table tbody');
    if (!tbody) return;

    // Prevent duplicate event listeners
    if (tbody.dataset.folderListenersAttached === 'true') {
      return;
    }
    tbody.dataset.folderListenersAttached = 'true';

    // Folder row click handling
    tbody.addEventListener('click', (e) => {
      const folderRow = e.target.closest('.folder-row');
      if (!folderRow) {
        // Check if it's the create folder button
        if (e.target.closest('#create-folder-btn')) {
          this.createFolder();
        }
        return;
      }

      // Handle toggle
      if (e.target.classList.contains('folder-toggle')) {
        this.toggleFolder(folderRow.dataset.folderId);
        return;
      }

      // Handle rename
      if (e.target.closest('.rename-folder')) {
        e.stopPropagation();
        this.renameFolder(folderRow.dataset.folderId);
        return;
      }

      // Handle delete
      if (e.target.closest('.delete-folder')) {
        e.stopPropagation();
        this.deleteFolder(folderRow.dataset.folderId);
        return;
      }

      // Select folder (click on row)
      this.selectFolder(folderRow.dataset.folderId);
    });

    // Drag and drop for folders
    this.setupFolderDragAndDrop();
  }

  attachEventListeners() {
    // Main event listeners for the module
    this.attachFolderEventListeners();
  }

  toggleFolder(folderId) {
    console.log('Toggling folder:', folderId);
    if (this.expandedFolders.has(folderId)) {
      this.expandedFolders.delete(folderId);
      console.log('Collapsed folder');
    } else {
      this.expandedFolders.add(folderId);
      console.log('Expanded folder');
    }
    this.render();
  }

  selectFolder(folderId) {
    // Just highlight the selected folder, don't filter documents
    this.selectedFolder = folderId === this.selectedFolder ? null : folderId;
    this.render();
  }

  filterDocumentsByFolder(folderId) {
    // Optional: could be used for a separate filter feature
    // Not called by default anymore
    const documentsTable = document.querySelector('.docs-table tbody');
    if (!documentsTable) return;

    const rows = documentsTable.querySelectorAll('tr[data-document-id]');
    rows.forEach(row => {
      const docFolderId = row.dataset.folderId;
      if (!folderId || docFolderId === folderId) {
        row.style.display = '';
      } else {
        row.style.display = 'none';
      }
    });
  }

  async createFolder() {
    const name = prompt('Enter folder name:');
    if (!name || name.trim() === '') return;

    const parentFolderId = this.selectedFolder;

    try {
      const response = await fetch(`/assets/${this.assetId}/document_folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          document_folder: {
            name: name,
            parent_folder_id: parentFolderId
          }
        })
      });

      if (response.ok) {
        await this.loadFolders();
        this.render();
      } else {
        const error = await response.json();
        alert(`Failed to create folder: ${error.errors || response.statusText}`);
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder');
    }
  }

  async renameFolder(folderId) {
    const folder = this.findFolder(folderId);
    if (!folder) return;

    const newName = prompt('Enter new folder name:', folder.name);
    if (!newName || newName === folder.name) return;

    try {
      const response = await fetch(`/assets/${this.assetId}/document_folders/${folderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          document_folder: {
            name: newName
          }
        })
      });

      if (response.ok) {
        await this.loadFolders();
        this.render();
      } else {
        const error = await response.json();
        alert(`Failed to rename folder: ${error.errors || response.statusText}`);
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      alert('Failed to rename folder');
    }
  }

  async deleteFolder(folderId) {
    const folder = this.findFolder(folderId);
    if (!folder) return;

    if (!confirm(`Delete folder "${folder.name}" and all its contents?`)) return;

    try {
      const response = await fetch(`/assets/${this.assetId}/document_folders/${folderId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        await this.loadFolders();
        this.render();
      } else {
        const error = await response.json();
        alert(`Failed to delete folder: ${error.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder');
    }
  }

  findFolder(folderId, folders = this.folders) {
    for (const folder of folders) {
      if (folder.id === folderId) return folder;
      if (folder.subfolders) {
        const found = this.findFolder(folderId, folder.subfolders);
        if (found) return found;
      }
    }
    return null;
  }

  setupFolderDragAndDrop() {
    const tbody = document.querySelector('.docs-table tbody');
    if (!tbody) return;

    // Prevent duplicate event listeners
    if (tbody.dataset.dragListenersAttached === 'true') {
      return;
    }
    tbody.dataset.dragListenersAttached = 'true';

    // Handle folder drag start
    tbody.addEventListener('dragstart', (e) => {
      const folderRow = e.target.closest('.folder-row[draggable="true"]');
      if (folderRow) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('folder-id', folderRow.dataset.folderId);
        folderRow.classList.add('dragging');
        console.log('Started dragging folder:', folderRow.dataset.folderId);
      }
    });

    tbody.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      const folderRow = e.target.closest('.folder-row');
      if (!folderRow) {
        // Remove highlight from all rows if not over a folder
        tbody.querySelectorAll('.drag-over-folder').forEach(row => {
          row.classList.remove('drag-over-folder');
        });
        return;
      }

      // Remove highlight from all other rows first
      tbody.querySelectorAll('.drag-over-folder').forEach(row => {
        if (row !== folderRow) {
          row.classList.remove('drag-over-folder');
        }
      });

      // Add highlight to the folder row
      if (!folderRow.classList.contains('drag-over-folder')) {
        console.log('Adding drag-over-folder to:', folderRow.querySelector('.folder-content strong')?.textContent);
        folderRow.classList.add('drag-over-folder');

        // Get brand colors from CSS variables
        const infoColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-info').trim();
        const warningColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-warning').trim();

        // Force style application with brand colors on all cells
        folderRow.style.backgroundColor = infoColor;
        folderRow.style.boxShadow = `inset 0 0 0 3px ${warningColor}`;
        folderRow.querySelectorAll('td').forEach(td => {
          td.style.backgroundColor = infoColor;
        });
      }
    });

    tbody.addEventListener('dragleave', (e) => {
      const folderRow = e.target.closest('.folder-row');
      if (!folderRow) return;

      // Only remove highlight if we're actually leaving the row
      const rect = folderRow.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX >= rect.right ||
        e.clientY < rect.top ||
        e.clientY >= rect.bottom
      ) {
        folderRow.classList.remove('drag-over-folder');
        folderRow.style.backgroundColor = '';
        folderRow.style.boxShadow = '';
        folderRow.querySelectorAll('td').forEach(td => {
          td.style.backgroundColor = '';
        });
      }
    });

    tbody.addEventListener('drop', async (e) => {
      const folderRow = e.target.closest('.folder-row');
      if (!folderRow) return;

      e.preventDefault();

      // Remove highlight and inline styles
      folderRow.classList.remove('drag-over-folder');
      folderRow.style.backgroundColor = '';
      folderRow.style.boxShadow = '';
      folderRow.querySelectorAll('td').forEach(td => {
        td.style.backgroundColor = '';
      });

      const draggedDocumentId = e.dataTransfer.getData('document-id');
      const draggedFolderId = e.dataTransfer.getData('folder-id');

      if (draggedDocumentId) {
        // Moving a document to folder
        await this.moveDocumentToFolder(draggedDocumentId, folderRow.dataset.folderId);
      } else if (draggedFolderId) {
        // Moving a folder into another folder
        await this.moveFolder(draggedFolderId, folderRow.dataset.folderId);
      }
    });

    // Clean up any lingering highlights and dragging state
    tbody.addEventListener('dragend', (e) => {
      tbody.querySelectorAll('.drag-over-folder').forEach(row => {
        row.classList.remove('drag-over-folder');
        row.style.backgroundColor = '';
        row.style.boxShadow = '';
        row.querySelectorAll('td').forEach(td => {
          td.style.backgroundColor = '';
        });
      });
      tbody.querySelectorAll('.dragging').forEach(row => {
        row.classList.remove('dragging');
      });
      console.log('Drag ended, cleaned up classes');
    });
  }

  async moveFolder(folderId, newParentId) {
    try {
      const response = await fetch(`/assets/${encodeURIComponent(this.assetId)}/document_folders/${encodeURIComponent(folderId)}/move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          parent_folder_id: newParentId
        })
      });

      if (response.ok) {
        await this.loadFolders();
        this.render();
      } else {
        const error = await response.json();
        alert(`Failed to move folder: ${error.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error moving folder:', error);
      alert('Failed to move folder');
    }
  }

  async moveDocumentToFolder(documentId, folderId) {
    try {
      const response = await fetch(`/documents/${encodeURIComponent(documentId)}/move_to_folder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          folder_id: folderId
        })
      });

      if (response.ok) {
        // Update the document row's folder_id attribute
        const tbody = document.querySelector('.docs-table tbody');
        const docRow = tbody.querySelector(`tr[data-document-id="${documentId}"]`);
        if (docRow) {
          docRow.dataset.folderId = folderId || '';
        }

        // Automatically expand the target folder and all its parents so the document is visible
        if (folderId) {
          this.expandFolderAndParents(folderId);
        }

        // Refresh both folders and documents
        await this.loadFolders();
        this.render();

        // Trigger document list refresh if available
        if (window.refreshDocuments) {
          window.refreshDocuments();
        }
      } else {
        alert('Failed to move document to folder');
      }
    } catch (error) {
      console.error('Error moving document:', error);
      alert('Failed to move document');
    }
  }

  expandFolderAndParents(folderId) {
    // Expand this folder
    this.expandedFolders.add(folderId);

    // Find the folder and expand all its parents
    const folder = this.findFolder(folderId);
    if (folder && folder.parent_folder_id) {
      this.expandFolderAndParents(folder.parent_folder_id);
    }
  }
}

// Export for use in other modules
window.DocumentFolderManager = DocumentFolderManager;

// Initialize on page load
function initializeDocumentFolders() {
  console.log('Initializing document folders...');
  const foldersContainer = document.getElementById('document-folders-container');
  console.log('Folders container:', foldersContainer);

  if (foldersContainer) {
    // Check if already initialized
    if (foldersContainer.dataset.initialized === 'true') {
      console.log('Document folders already initialized, skipping...');
      return;
    }

    const assetId = foldersContainer.dataset.assetId;
    console.log('Asset ID:', assetId);

    if (assetId) {
      foldersContainer.dataset.initialized = 'true';

      const manager = new DocumentFolderManager(assetId, 'document-folders-container');
      manager.init();

      // Make manager globally accessible for document drag-drop
      window.documentFolderManager = manager;
      console.log('Document folder manager initialized');
    } else {
      console.log('No asset ID found on container');
    }
  } else {
    console.log('No document-folders-container found');
  }
}

// Initialize on page load
document.addEventListener('turbo:load', initializeDocumentFolders);
document.addEventListener('DOMContentLoaded', initializeDocumentFolders);

// Listen for turbo-frame loads (for tab content)
document.addEventListener('turbo:frame-load', function(event) {
  console.log('Turbo frame loaded:', event.target.id);
  // Initialize after a short delay to ensure DOM is ready
  setTimeout(initializeDocumentFolders, 100);
});

// Also listen for turbo:render which happens after Turbo updates the page
document.addEventListener('turbo:render', function() {
  console.log('Turbo render event');
  setTimeout(initializeDocumentFolders, 100);
});

// Also try immediate initialization in case the DOM is already loaded
if (document.readyState === 'loading') {
  // Still loading, wait for DOMContentLoaded
} else {
  // DOM is already ready
  initializeDocumentFolders();
}
